import type { ComponentType, ReactElement } from 'react';
import { type DateTime } from 'luxon';
import { Info } from 'lucide-react';
import { type z } from 'zod';
import { ulid } from 'ulid';
import { Tooltip, TooltipContent, TooltipPortal, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { MetadataCheckbox } from '@/components/inspector/metadata-accordion/metadata-checkbox';
import { MultiSelect } from '@/components/inspector/metadata-accordion/metadata-multiselect';
import { MetadataSelect } from '@/components/inspector/metadata-accordion/metadata-select';
import { DateTimeGranularity, getDateTimeInputConfig } from '@/utilities/date';
import { Options } from '@/components/inspector/entity-fields/form/options';
import { QualitySlider } from '@/components/ui/quality-slider';
import { DateTimePicker } from '@/components/datetime-picker';
import { TagSelector } from '@/components/tag-selector';
import { DatePicker } from '@/components/ui/datepicker';
import { type MetadataField } from '@/types/metadata';
import { Dropzone } from '@/components/ui/dropzone';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export type MetadataTypeMap = {
  string: string;
  text: string;
  integer: number;
  float: number;
  boolean: boolean;
  tag_selector: string;
  date: Date;
  datetime: DateTime;
  select: string;
  multi_select: Array<string>;
  options: object;
  file: string;
  slider: number;
  dropzone: string;
  quality_slider: number;
};

const FieldComponents: Record<keyof MetadataTypeMap, ComponentType<any>> = {
  string: Input,
  text: Textarea,
  integer: Input,
  float: Input,
  boolean: MetadataCheckbox,
  tag_selector: TagSelector,
  date: DatePicker,
  datetime: DateTimePicker,
  select: MetadataSelect,
  multi_select: MultiSelect,
  options: Options,
  file: Input,
  slider: Slider,
  dropzone: Dropzone,
  quality_slider: QualitySlider,
};

export type FieldProps = Pick<MetadataField, 'name' | 'slug' | 'type' | 'description' | 'options'> & {
  'disabled': boolean;
  'noLabel'?: boolean;
  'appendIcon'?: ReactElement;
  'placeholder'?: string;
  'validation': z.ZodTypeAny;
  'min'?: number;
  'max'?: number;
  'data-cy'?: string;
  'readOnly'?: boolean;
  'isLoading'?: boolean;
  'onClick'?: (...args: Array<any>) => void;
  'onChange'?: (...args: Array<any>) => void;
  'value'?: string;
  'checked'?: boolean;
  'withUploader'?: boolean;
  'ref'?: HTMLInputElement;
  'accept'?: string;
  'onInput'?: (...args: Array<any>) => void;
  'step'?: number;
  'rows'?: number;
  'tagId'?: string;
  'parentTag'?: { id: string; name: string } | null;
  /** For a `tag_selector` field: false defers persisting the selection to the form's own submit. */
  'autoSave'?: boolean;
  /** For a `tag_selector` field: true restricts the offered parents to root (top-level) tags. */
  'rootOnly'?: boolean;
  'isRequired'?: boolean;
  'autoFocus'?: boolean;
  'hidden'?: boolean;
  'formLabelClassName'?: string;
  'descriptionAsText'?: boolean;
};

const ClearFieldButton = ({ onClick }: { onClick: () => void }): ReactElement => {
  const tCommon = useTranslations('common');

  return (
    <Button
      type="button"
      onClick={onClick}
      variant="ghost"
      className="mx-0.5 min-h-full rounded-[7px] border-0 p-0.75 text-xs whitespace-nowrap text-icon-base! duration-150 hover:bg-transparent! hover:text-text-base! focus-visible:bg-ui-base focus-visible:ring-0"
      aria-label={tCommon('clearField')}
    >
      {tCommon('clear')}
    </Button>
  );
};

export const renderField = (form: any, field: FieldProps): JSX.Element | null => {
  const Component = FieldComponents[field.type as keyof MetadataTypeMap];

  if (!Component) {
    return null;
  }

  const isRequired =
    field.isRequired ??
    (field.type === 'boolean'
      ? false
      : field.validation
        ? !field.validation.isOptional() && !field.validation.isNullable()
        : false);

  return (
    <FormField
      key={field.slug}
      name={field.slug}
      control={form.control}
      render={({ field: controllerField }) => (
        <FormItem
          className={cn(
            'flex',
            field.type === 'boolean' ? 'flex-row gap-x-3' : 'flex-col',
            field.type === 'boolean' && field.disabled && 'opacity-40',
            field.hidden && 'hidden',
          )}
        >
          <div
            className={cn('flex', field.descriptionAsText ? 'flex-col' : 'items-center gap-2', {
              'min-w-0 grow': field.type === 'boolean',
            })}
          >
            {!field.noLabel && (
              <FormLabel
                className={cn(
                  'leading-4',
                  isRequired && 'after:ml-0.5 after:text-red-500 after:content-["*"]',
                  field.formLabelClassName,
                )}
              >
                {field.name}
              </FormLabel>
            )}
            {field.description &&
              (field.descriptionAsText ? (
                <FormDescription>{field.description}</FormDescription>
              ) : (
                <TooltipProvider key={ulid()} delayDuration={100}>
                  <Tooltip>
                    <TooltipTrigger asChild className="flex opacity-50 duration-150 hover:opacity-100">
                      <Info className="icon-3.5 text-text-gray" />
                    </TooltipTrigger>
                    <TooltipPortal>
                      <TooltipContent className="max-w-75">
                        <p>{field.description}</p>
                      </TooltipContent>
                    </TooltipPortal>
                  </Tooltip>
                </TooltipProvider>
              ))}
          </div>
          <FormControl
            className={cn({
              'mt-0! shrink-0': field.type === 'boolean',
            })}
          >
            <Component
              {...controllerField}
              name={field.slug}
              readOnly={field.readOnly ?? false}
              // indeterminate
              placeholder={
                field.type === 'date'
                  ? getDateTimeInputConfig(DateTimeGranularity.Day).placeholder
                  : field.type === 'datetime'
                    ? undefined
                    : (field.placeholder ?? field.name)
              }
              {...(field.options && { options: field.options })}
              onChange={(value: any) => {
                if (['slider', 'quality_slider'].includes(field.type)) {
                  return;
                }
                if (field.type === 'multi_select') {
                  const newValues = (value as Array<{ value: string; label: string }>).map((option) => option.value);
                  controllerField.onChange(newValues);
                } else {
                  controllerField.onChange(value);
                }
              }}
              {...(field.type === 'slider' || field.type === 'quality_slider'
                ? {
                    onValueChange: (value: any) => {
                      controllerField.onChange(value);
                    },
                  }
                : {})}
              {...(['boolean', 'file'].includes(field.type) &&
                field.onClick && {
                  onClick: field.onClick,
                })}
              {...(field.autoFocus && { autoFocus: field.autoFocus })}
              {...(field.type === 'file' && field.value && { value: field.value })}
              {...((field.type === 'slider' || field.type === 'quality_slider') && {
                value: controllerField.value ? [controllerField.value] : [0],
              })}
              {...(field.type === 'slider' && field.step && { step: field.step })}
              {...(field.type === 'boolean' && field.checked && { checked: controllerField.value })}
              {...(field.type === 'float' && { type: 'number' })}
              {...(field.min !== undefined && { min: field.min })}
              {...(field.max !== undefined && { max: field.max })}
              {...(field.type === 'tag_selector' && field.tagId && { tagId: field.tagId })}
              {...(field.type === 'tag_selector' && { parentTag: field.parentTag ?? null })}
              {...(field.type === 'tag_selector' && field.autoSave === false && { autoSave: false })}
              {...(field.type === 'tag_selector' && field.rootOnly && { rootOnly: true })}
              {...(field.type === 'file' &&
                field.withUploader && {
                  withUploader: field.withUploader,
                  inputRef: field.ref,
                  accept: field.accept,
                  onInput: field.onInput,
                })}
              {...(field.disabled && { disabled: field.disabled })}
              {...(field.rows && { rows: field.rows })}
              {...(field.isLoading && { isLoading: field.isLoading })}
              data-cy={field['data-cy'] ?? `${field.slug}-field`}
              className={cn(
                // field.className,
                field.type === 'text' && 'w-full',
                ['text', 'string', 'float', 'integer', 'select', 'date', 'datetime', 'file'].includes(field.type) &&
                  'bg-input-background focus-within:ring-offset-ui-base',
                ['date', 'datetime'].includes(field.type) &&
                  'focus-within:ring-offset-ui-base hover:bg-white dark:hover:bg-neutral-950',
                ['text', 'string', 'float', 'integer', 'date', 'datetime'].includes(field.type) && 'pr-0!',
                field.type === 'file' && 'pr-0',
              )}
              // Include Input's `appendIcon` property with an "X" button only if field type is
              // text, string, date or integer and if controllerField.value contains value
              // and if the field is not disabled
              {...(['text', 'string', 'integer', 'date', 'datetime', 'file'].includes(field.type) && {
                appendIcon:
                  controllerField.value && !field.disabled && field.type !== 'file' ? (
                    <ClearFieldButton
                      onClick={() => {
                        controllerField.onChange('');
                        if (field.type === 'date' || field.type === 'datetime') {
                          queueMicrotask(() => {
                            void form.trigger(field.slug);
                          });
                        }
                      }}
                    />
                  ) : (
                    field.appendIcon
                  ),
              })}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
